import React, { useEffect, useMemo, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Users, Calendar, ArrowRight, Sparkles } from 'lucide-react';
interface TransitionScreenProps {
  onEnterApp: () => void;
}
export function TransitionScreen({
  onEnterApp
}: TransitionScreenProps) {
  const [phase, setPhase] = useState(1);
  const [activeCard, setActiveCard] = useState(0);
  // Auto-advance phases
  useEffect(() => {
    const timers = [setTimeout(() => setPhase(2), 3000), setTimeout(() => setPhase(3), 6000), setTimeout(() => setPhase(4), 9000), setTimeout(() => setPhase(5), 12000),
    // Auto-enter app after Phase 5 plays
    setTimeout(() => onEnterApp(), 18000)];
    return () => timers.forEach(clearTimeout);
  }, [onEnterApp]);
  // Cycle cards in Phase 4
  useEffect(() => {
    if (phase === 4) {
      const interval = setInterval(() => {
        setActiveCard(c => (c + 1) % 3);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [phase]);
  // Generate neural particles
  const particles = useMemo(() => {
    return Array.from({
      length: 40
    }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      targetX: 50 + (Math.random() * 20 - 10),
      targetY: 50 + (Math.random() * 20 - 10),
      size: Math.random() * 2 + 1,
      delay: Math.random() * 2
    }));
  }, []);
  const cards = [{
    icon: Phone,
    title: 'AI Call Handling'
  }, {
    icon: Users,
    title: 'Caller Experience'
  }, {
    icon: Calendar,
    title: 'Calendar Booking'
  }];
  return <div className="fixed inset-0 z-[200] bg-black text-white overflow-hidden font-sans flex items-center justify-center" data-id="element-2352">
      {/* Ambient Background Glow */}
      <motion.div animate={{
      opacity: phase >= 3 ? 0.4 : 0.1,
      scale: phase >= 3 ? 1.2 : 1
    }} transition={{
      duration: 3,
      ease: 'easeInOut'
    }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-2353">
        <div className="w-[600px] h-[600px] bg-vexu/20 rounded-full blur-[120px]" data-id="element-2354" />
      </motion.div>

      {/* Phase 1 & 2: Neural Particles */}
      <AnimatePresence data-id="element-2355">
        {phase <= 2 && <motion.div exit={{
        opacity: 0,
        scale: 1.1
      }} transition={{
        duration: 1.5
      }} className="absolute inset-0 pointer-events-none" data-id="element-2356">
            {particles.map(p => <motion.div key={p.id} initial={{
          left: `${p.x}%`,
          top: `${p.y}%`,
          opacity: 0
        }} animate={{
          left: `${p.targetX}%`,
          top: `${p.targetY}%`,
          opacity: [0, 0.8, 0.2, 0.8]
        }} transition={{
          duration: 4,
          delay: p.delay,
          ease: 'easeInOut',
          opacity: {
            repeat: Infinity,
            duration: 2
          }
        }} className="absolute rounded-full bg-vexu shadow-[0_0_8px_rgba(0,200,83,0.8)]" style={{
          width: p.size,
          height: p.size
        }} data-id="element-2357" />)}
          </motion.div>}
      </AnimatePresence>

      {/* Phase 2: Text Reveal */}
      <AnimatePresence data-id="element-2358">
        {phase === 2 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20,
        filter: 'blur(10px)'
      }} transition={{
        duration: 1.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="absolute inset-0 flex flex-col items-center justify-center text-center z-10" data-id="element-2359">
            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5,
          duration: 1
        }} className="text-xl md:text-2xl text-zinc-400 font-medium tracking-wide mb-4" data-id="element-2360">
              This is not a demo.
            </motion.p>
            <motion.h1 initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 1.5,
          duration: 1.5,
          ease: 'easeOut'
        }} className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-white/60 tracking-tight" style={{
          textShadow: '0 0 40px rgba(0,200,83,0.3)'
        }} data-id="element-2361">
              This is Vexu.
            </motion.h1>
          </motion.div>}
      </AnimatePresence>

      {/* Phase 3 & 4: Smartphone Silhouette & Capabilities */}
      <AnimatePresence data-id="element-2362">
        {(phase === 3 || phase === 4) && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 1.1,
        filter: 'blur(10px)'
      }} transition={{
        duration: 1.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="absolute inset-0 flex items-center justify-center z-20" data-id="element-2363">
            {/* Phone Silhouette */}
            <div className="relative w-[280px] h-[580px] rounded-[48px] border border-white/10 bg-black/40 backdrop-blur-xl shadow-[0_0_60px_rgba(0,200,83,0.15)] flex flex-col items-center justify-center overflow-hidden" data-id="element-2364">
              {/* Energy lines flowing in */}
              <motion.div animate={{
            backgroundPosition: ['0% 0%', '0% 200%']
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute inset-0 opacity-30" style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(0,200,83,0.2) 50%, transparent 100%)',
            backgroundSize: '100% 200%'
          }} data-id="element-2365" />

              {/* Phase 4: Capability Flashes */}
              <AnimatePresence mode="wait" data-id="element-2366">
                {phase === 4 && <motion.div key={activeCard} initial={{
              opacity: 0,
              y: 20,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -20,
              scale: 0.9
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} className="relative z-10 flex flex-col items-center gap-4" data-id="element-2367">
                    <div className="w-16 h-16 rounded-2xl bg-vexu/10 border border-vexu/30 flex items-center justify-center shadow-[0_0_30px_rgba(0,200,83,0.3)]" data-id="element-2368">
                      {createElement(cards[activeCard].icon, {
                  className: 'w-8 h-8 text-vexu'
                })}
                    </div>
                    <h3 className="text-lg font-bold text-white tracking-wide" data-id="element-2369">
                      {cards[activeCard].title}
                    </h3>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Phase 5: Final — auto-transitions to app */}
      <AnimatePresence data-id="element-2370">
        {phase === 5 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 1.05,
        filter: 'blur(10px)'
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} className="absolute inset-0 flex flex-col items-center justify-center z-30 px-6 text-center" data-id="element-2371">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 1
        }} className="mb-8" data-id="element-2372">
              <Sparkles className="w-8 h-8 text-vexu mx-auto mb-6 opacity-80" data-id="element-2373" />
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4" data-id="element-2374">
                Experience Vexu
                <br data-id="element-2375" />
                in real time.
              </h2>
            </motion.div>

            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          textShadow: ['0 0 0px rgba(0,200,83,0)', '0 0 16px rgba(0,200,83,0.6)', '0 0 0px rgba(0,200,83,0)']
        }} transition={{
          opacity: {
            delay: 1.5,
            duration: 1
          },
          textShadow: {
            delay: 2,
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} className="text-lg font-semibold text-vexu tracking-wide mb-3" data-id="element-2376">
              Use any number. No OTP. No setup required.
            </motion.p>

            {/* Loading indicator before auto-transition */}
            <motion.div initial={{
          opacity: 0,
          width: 0
        }} animate={{
          opacity: 1,
          width: '120px'
        }} transition={{
          delay: 3,
          duration: 3,
          ease: 'linear'
        }} className="h-[2px] bg-vexu rounded-full mt-6 shadow-[0_0_10px_rgba(0,200,83,0.8)]" data-id="element-2377" />

            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 3.5,
          duration: 1
        }} className="absolute bottom-10 flex flex-col items-center gap-2" data-id="element-2378">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600" data-id="element-2379">
                Contact
              </span>
              <div className="flex items-center gap-4" data-id="element-2380">
                <a href="mailto:fadi@vexu.ai" className="text-xs font-medium text-zinc-400 hover:text-vexu transition-colors" data-id="element-2381">
                  fadi@vexu.ai
                </a>
                <span className="text-zinc-700" data-id="element-2382">·</span>
                <a href="tel:+971585377090" className="text-xs font-medium text-zinc-400 hover:text-vexu transition-colors" data-id="element-2383">
                  +971 58 537 7090
                </a>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}